import { ErrorMessage, Field, FieldProps, Form, Formik, FormikProps } from "formik";
import { useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import { ButtonCustom } from "../../../../components/custom/button-custom";
import { showToast } from "../../../../components/custom/custom-toast";
import DateCustomInput from "../../../../components/custom/date-input-custom";
import ImageCustom from "../../../../components/custom/image-custom";
import { LoadingCustom } from "../../../../components/custom/loading-custom";
import { ModalCustom } from "../../../../components/custom/modal-custom";
import { IconEllipse, IconUserDefault } from "../../../../components/icons/icon-group";
import { GENDER } from "../../../../constants/status";
import { useFetchData } from "../../../../hooks/useFetch";
import useUpload from "../../../../hooks/useUpload";
import { EmployeeManagerRequest } from "../../../../models/requests/employee-manager-request";
import { EmployeeResponse } from "../../../../models/response/employee";
import { API_ADMIN_ROUTER, API_UPLOAD_ROUTER } from "../../../../services/api-config";
import { checkUsername, handleIsValidInput } from "../../../../utils/helpers";
import { convertDateFormat, getTodayDate } from "../../../../utils/time-utils";

interface AdminUpdateProps {
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
  adminId: string;
  setRefresh: (value: boolean) => void;
}

export const AdminUpdate = ({ isOpen, setIsOpen, adminId, setRefresh }: AdminUpdateProps) => {
  const formikRef = useRef<FormikProps<any>>(null);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [isImageLocal, setIsImageLocal] = useState(false);
  const [imageUpload, setImageUpload] = useState("");
  const [imageDisplay, setImageDisplay] = useState("");
  const [dataDetail, setDataDetail] = useState<EmployeeResponse>(new EmployeeResponse());

  const { request: fetchDataDetail, baseResponse: responsesDetailEmployee } = useFetchData.Get<EmployeeResponse>();
  const { request: fetchDataCreate, baseResponse: responsesUpdate } = useFetchData.Post();
  const { uploadFiles: onUploadFile, baseResponse: responseUpload } = useUpload<any>(API_UPLOAD_ROUTER.API_UPLOAD);

  useEffect(() => {
    if (!isOpen) {
      formikRef.current?.resetForm();
      setImageDisplay("");
    }
  }, [isOpen]);

  useEffect(() => {
    fetchDataDetail({
      endPoint: API_ADMIN_ROUTER.API_DETAIL_ADMIN(adminId),
    });
  }, [adminId]);

  useEffect(() => {
    if (!responsesDetailEmployee) return;
    if (responsesDetailEmployee.status === 200) {
      setImageDisplay(responsesDetailEmployee.data.avatar);
      setImageUpload(responsesDetailEmployee.data.avatar);
      setDataDetail(responsesDetailEmployee.data);
    }
  }, [responsesDetailEmployee]);

  const handleUpdate = (values: any) => {
    const newData = new EmployeeManagerRequest();
    newData.name = values.name;
    newData.username = values.username;
    newData.birthday = values.birthday;
    newData.gender = values.gender;
    newData.phone = values.phone;
    newData.address = values.address;
    newData.avatar = imageUpload;
    newData.joining_date = values.joining_date;
    if (adminId) {
      fetchDataCreate({
        endPoint: API_ADMIN_ROUTER.API_UPDATE_ADMIN(adminId),
        data: newData,
      });
    }
  };

  useEffect(() => {
    if (!responsesUpdate) return;
    if (responsesUpdate.status === 200) {
      showToast({ message: "Chỉnh sửa thành công", type: "success" });
      setIsOpen(false);
      setRefresh(true);
    } else {
      showToast({ message: responsesUpdate.message, type: "error" });
    }
  }, [responsesUpdate]);

  useEffect(() => {
    if (!responseUpload) return;
    if (responseUpload.status === 200 && responseUpload.data.length > 0) {
      setImageUpload(responseUpload.data[0]);
    }
  }, [responseUpload]);

  const SubmittedForm = Yup.object().shape({
    name: Yup.string()
      .min(2, "Độ dài tối thiểu 2 ký tự")
      .max(100, "Độ dài tối đa 100 ký tự")
      .required("Độ dài tối thiểu 2 ký tự"),
    username: Yup.string()
      .min(2, "Độ dài tối thiểu 2 ký tự")
      .max(50, "Độ dài tối đa 50 ký tự")
      .required("Độ dài tối thiểu 2 ký tự"),
    phone: Yup.string().min(10, "Số điện thoại chưa đúng 10 hoặc 11 ký tự"),
    address: Yup.string().max(100, "Độ dài tối đa 100 ký tự"),
  });

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files; // Lấy tệp hình ảnh
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImageDisplay(reader.result as string); // Cập nhật URL hình ảnh
        setIsImageLocal(true);
      };
      reader.readAsDataURL(file[0]); // Đọc tệp dưới dạng URL
      onUploadFile(file);
    }
  };

  const formatFieldDate = (value: string) => {
    return value ? convertDateFormat(value, "yyyy-MM-dd", "dd/MM/yyyy") : "";
  };

  useEffect(() => {
    if (dataDetail._id !== "") {
      setIsDataLoaded(true); // Mark data as fully loaded when both profile and branch data are available
    }
  }, [dataDetail]);

  if (!isDataLoaded) {
    return <LoadingCustom />;
  }

  return (
    <Formik
      innerRef={formikRef}
      initialValues={dataDetail}
      validationSchema={SubmittedForm}
      onSubmit={(values) => {
        handleUpdate(values);
      }}
    >
      {({ values, submitForm, resetForm, setFieldValue }) => {
        const bodyModal = () => {
          return (
            <Form className="space-y-5">
              <button type="button" className="flex flex-col items-center justify-center w-full relative">
                <span className="w-24 h-24 relative">
                  <ImageCustom
                    url={`${imageDisplay || IconUserDefault}`}
                    isLocal={isImageLocal}
                    className="w-24 h-24 rounded-full overflow-hidden object-cover"
                  />
                  <input
                    type="file"
                    id="fileUpload"
                    className="absolute inset-0 opacity-0 cursor-pointer"
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                  <IconEllipse className="w-24 h-24 absolute inset-0 flex items-center justify-center" />
                </span>
                <label htmlFor="fileUpload" className="mt-1 text-blue-500 text-xs font-medium cursor-pointer">
                  Tải hình đại diện
                </label>
              </button>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {/* Tên nhân viên */}
                <div>
                  <label htmlFor="name">
                    Tên nhân viên <span className="text-red-500">*</span>
                  </label>
                  <Field
                    value={values.name}
                    name="name"
                    type="text"
                    id="name"
                    placeholder="Nhập thông tin"
                    className="form-input"
                    maxLength={100}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const inputValue = e.target.value;
                      if (inputValue.length <= 100 && handleIsValidInput(inputValue)) {
                        console.log("inputValue", inputValue);
                        setFieldValue("name", inputValue);
                      }
                    }}
                  />
                  <ErrorMessage name="name" component="div" className="text-red-500 text-xs mt-1" />
                </div>
                <div>
                  <label htmlFor="username">
                    Tên đăng nhập <span className="text-red-500">*</span>
                  </label>
                  <Field
                    value={values.username}
                    name="username"
                    type="text"
                    id="username"
                    placeholder="Nhập thông tin"
                    className="form-input"
                    maxLength={50}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const inputValue = e.target.value;
                      if (inputValue.length <= 50 && checkUsername(inputValue)) {
                        setFieldValue("username", inputValue);
                      }
                    }}
                  />
                  <ErrorMessage name="username" component="div" className="text-red-500 text-xs mt-1" />
                </div>

                {/* Giới tính */}
                <div>
                  <label htmlFor="gender">Giới tính</label>
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center mt-2">
                      <Field
                        type="radio"
                        name="gender"
                        value={GENDER.MALE}
                        className="form-radio"
                        onChange={() => setFieldValue("gender", GENDER.MALE)}
                      />
                      <span className="ml-2">Nam</span>
                    </label>
                    <label className="flex items-center mt-2">
                      <Field
                        type="radio"
                        name="gender"
                        value={GENDER.FEMALE}
                        className="form-radio"
                        onChange={() => setFieldValue("gender", GENDER.FEMALE)}
                      />
                      <span className="ml-2">Nữ</span>
                    </label>
                  </div>
                </div>

                {/* Ngày sinh */}
                <div>
                  <label htmlFor="birthday">Ngày sinh</label>
                  <Field name="birthday">
                    {() => (
                      <DateCustomInput
                        maxDate={getTodayDate()}
                        label="Ngày sinh"
                        value={values.birthday}
                        onChange={(date) => setFieldValue("birthday", date)}
                      />
                    )}
                  </Field>
                </div>

                {/* Ngày làm việc */}
                <div>
                  <label htmlFor="joining_date">Ngày làm việc</label>
                  <Field name="joining_date">
                    {({ field, form }: FieldProps) => (
                      <DateCustomInput
                        label="Ngày làm việc"
                        value={values.joining_date}
                        onChange={(date) => setFieldValue("joining_date", date)}
                      />
                    )}
                  </Field>
                </div>

                {/* Số điện thoại */}
                <div>
                  <label htmlFor="phone">Số điện thoại</label>
                  <Field
                    value={values.phone}
                    name="phone" // Đổi tên trường ở đây
                    type="text"
                    id="phone"
                    placeholder="Nhập thông tin"
                    className="form-input"
                    minLength={10}
                    maxLength={11}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      let inputValue = e.target.value.replace(/\D/g, "");
                      if (inputValue.length > 0 && !inputValue[0].startsWith("0")) {
                        inputValue = "0" + inputValue;
                      }
                      if (inputValue.length <= 11) {
                        setFieldValue("phone", inputValue);
                      }
                    }}
                  />
                  <ErrorMessage name="phone" component="div" className="text-red-500 text-xs mt-1" />
                </div>
              </div>
              <div>
                <label htmlFor="address">Địa chỉ</label>
                <Field
                  value={values.address}
                  name="address"
                  type="text"
                  id="address"
                  maxLength={100}
                  placeholder="Nhập thông tin"
                  className="form-input"
                />
                <ErrorMessage name="address" component="div" className="text-red-500 text-xs mt-1" />
              </div>
            </Form>
          );
        };

        const footerModal = () => (
          <>
            <ButtonCustom
              type="button"
              text="Hủy"
              onClick={() => {
                resetForm();
                setIsOpen(false);
              }}
              className="bg-gray-100 text-gray-500 hover:bg-gray-400 hover:text-white"
            />
            <ButtonCustom type="button" text="Lưu" onClick={submitForm} />
          </>
        );

        return (
          <ModalCustom
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            titleModal="Chỉnh sửa admin"
            bodyModal={bodyModal()}
            footerModal={footerModal()}
            modalSize="80%"
          />
        );
      }}
    </Formik>
  );
};
